/**
 * Header do Sistema MEDICARE
 * Barra superior com navegação e perfil do usuário
 */

import React from 'react';
import {
  Flex,
  IconButton,
  Text,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Box,
  useColorModeValue,
  useColorMode,
  Badge,
} from '@chakra-ui/react';
import {
  FiMenu,
  FiSun,
  FiMoon,
  FiUser,
  FiLogOut,
  FiBell,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '../../utils/avatarUtils';

interface HeaderProps {
  onOpen: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpen }) => {
  const { usuario, logout } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.200');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getUserTypeColor = (tipo: string) => {
    switch (tipo) {
      case 'admin':
        return 'red';
      case 'medico':
        return 'blue';
      case 'enfermeiro':
        return 'green';
      case 'paciente':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getUserTypeLabel = (tipo: string) => {
    switch (tipo) {
      case 'admin':
        return 'Administrador';
      case 'medico':
        return 'Médico';
      case 'enfermeiro':
        return 'Enfermeiro';
      case 'paciente':
        return 'Paciente';
      default:
        return tipo;
    }
  };

  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={bgColor}
      borderBottomWidth="1px"
      borderBottomColor={borderColor}
      justifyContent={{ base: 'space-between', md: 'flex-end' }}
      position="sticky"
      top="0"
      zIndex="sticky"
      boxShadow="sm"
    >
      {/* Botão do menu mobile */}
      <IconButton
        display={{ base: 'flex', md: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="Abrir menu"
        icon={<FiMenu />}
      />

      {/* Logo/Título para mobile */}
      <Text
        display={{ base: 'flex', md: 'none' }}
        fontSize="lg"
        fontFamily="heading"
        fontWeight="bold"
        color="medicare.500"
      >
        MEDICARE
      </Text>

      {/* Ações do header */}
      <HStack spacing={{ base: 2, md: 4 }}>
        {/* Botão de notificações */}
        <IconButton
          size="lg"
          variant="ghost"
          aria-label="Notificações"
          icon={<FiBell />}
          position="relative"
        >
          {/* Badge de notificações não lidas */}
          <Badge
            colorScheme="red"
            variant="solid"
            fontSize="xs"
            position="absolute"
            top="-1"
            right="-1"
            borderRadius="full"
            minW="5"
            h="5"
          >
            3
          </Badge>
        </IconButton>

        {/* Botão de modo escuro/claro */}
        <IconButton
          size="lg"
          variant="ghost"
          aria-label="Alternar tema"
          icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
          onClick={toggleColorMode}
        />

        {/* Menu do usuário */}
        <Menu>
          <MenuButton
            py={2}
            transition="all 0.3s"
            _focus={{ boxShadow: 'none' }}
          >
            <HStack spacing={3}>
              <Avatar
                size="sm"
                name={usuario?.nome}
                src={getAvatarUrl(usuario?.avatar)}
                bg={`${getUserTypeColor(usuario?.tipo_usuario || '')}.500`}
              />
              <Box display={{ base: 'none', md: 'flex' }} flexDirection="column" alignItems="flex-start">
                <Text fontSize="sm" fontWeight="medium">
                  {usuario?.nome}
                </Text>
                <Badge
                  size="sm"
                  colorScheme={getUserTypeColor(usuario?.tipo_usuario || '')}
                  variant="subtle"
                >
                  {getUserTypeLabel(usuario?.tipo_usuario || '')}
                </Badge>
              </Box>
            </HStack>
          </MenuButton>
          <MenuList>
            <MenuItem icon={<FiUser />} onClick={() => navigate('/perfil')}>
              Meu Perfil
            </MenuItem>
            <MenuDivider />
            <MenuItem icon={<FiLogOut />} onClick={handleLogout} color="red.500">
              Sair
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </Flex>
  );
};

export default Header;